import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useEffect, useState } from 'react';
import {
	View,
	FlatList,
	TouchableOpacity,
	StyleSheet,
	ScrollView,
} from 'react-native';
import { ZenIcon, ZenPopover, ZenText } from 'zen-core-elements';

export type categories = {
	id: number;
	categoryName: string;
	categoryImageUrl: string | null;
	sortOrder: number;
	parentCategoryId: number;
	subCategory: categories[];
};

interface Props {
	data: categories[];
	onCategorySelect: (category: number) => void;
	refresh: boolean
}

const CategorySelector: React.FC<Props> = ({ data, onCategorySelect, refresh }) => {
	const [selectedId, setSelectedId] = useState<number | null>(-1);
	const [navPath, setNavPath] = useState<number[]>([]);
	const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

	useEffect(() => {
		if (refresh) {
			setSelectedId(-1)
			setNavPath([])
			setOpenPopoverId(null)
		}
	}, [refresh])
	/** find current sublevel using the path */
	const getChildrenByPath = (
		path: number[],
		nodes: categories[]
	): categories[] => {
		let current = nodes;
		for (let id of path) {
			const found = current.find((c) => c.id === id);
			if (!found) return [];
			current = found.subCategory ?? [];
		}
		return current;
	};

	/** Recursively check if a node or its children contains the selected ID */
	const containsSelectedId = (node: categories, targetId: number | null): boolean => {
		if (!targetId) return false;
		if (node.id === targetId) return true;
		return (node.subCategory ?? []).some((child) => containsSelectedId(child, targetId));
	};

	const handleRootPress = (item: categories) => {
		const hasSub = (item.subCategory ?? []).length > 0;
		if (hasSub) {
			if (openPopoverId === item.id) {
				setOpenPopoverId(null);
				setNavPath([]);
			} else {
				setOpenPopoverId(item.id);
				setNavPath([item.id]); // Reset path to this root
			}
		} else {
			setSelectedId(item.id);
			onCategorySelect(item?.id);
			setOpenPopoverId(null);
			setNavPath([]);
		}
	};

	const handleSubPress = (node: categories) => {
		if ((node.subCategory ?? []).length > 0) {
			// Drill down
			setNavPath((prev) => [...prev, node.id]);
		} else {
			// Select leaf
			setSelectedId(node.id);
			onCategorySelect(node?.id);
			setOpenPopoverId(null);
			setNavPath([]);
		}
	};

	const renderItem = ({ item }: { item: categories }) => {
		const hasSub = (item.subCategory ?? []).length > 0;
		// Check if this root item is part of the selected path
		const isRootActive = containsSelectedId(item, selectedId);

		// Get sub-items for the popover
		const currentSubItems = openPopoverId === item.id
			? getChildrenByPath(navPath, data)
			: [];

		return (
			<ThemeConsumer>
				{({ theme }) => {
					const styles = style(theme);
					return (
						<ZenPopover
							isVisible={openPopoverId === item.id}
							onRequestClose={() => {
								setOpenPopoverId(null);
								setNavPath([]);
							}}
							from={
								<TouchableOpacity
									style={[styles.chip, isRootActive && styles.chipActive]}
									onPress={() => handleRootPress(item)}
								>
									<ZenText
										content={item.categoryName}
										style={[styles.chipText, isRootActive && styles.chipTextActive]}
									/>
									{hasSub && (
										<ZenIcon
											name='caretdown'
											type='antdesign'
											size={9}
											color={isRootActive ? theme?.primary : theme?.grey}
										/>
									)}
								</TouchableOpacity>
							}
							RenderContent={() => (
								<View style={styles.subCategoryContainer}>
									<ZenText
										content={'Select ' + item.categoryName}
										style={styles.popoverHeader}
									/>

									{/* Back Button */}
									{navPath.length > 1 && (
										<TouchableOpacity
											style={[styles.subCategoryItem, { justifyContent: "flex-start", gap: 10, alignItems: "center" }]}
											onPress={() =>
												setNavPath((prev) => prev.slice(0, prev.length - 1))
											}
										>
											<ZenIcon
												name='keyboard-backspace'
												type='material-community'
												size={18}
												color={theme?.lightDark}
											/>
											<ZenText content='Back' style={styles.subCategoryText} />
										</TouchableOpacity>
									)}
									<View style={{ zIndex: 1000 }}>
										<ScrollView
											nestedScrollEnabled={true}
											keyboardShouldPersistTaps="handled"
											showsVerticalScrollIndicator
										>
											{currentSubItems.map((n) => {
												// Check if this specific list item is in the selected path
												const isItemActive = containsSelectedId(n, selectedId);
												const isExactSelection = selectedId === n.id;
												const hasChildren = (n.subCategory ?? []).length > 0;

												return (
													<TouchableOpacity
														key={n.id}
														style={[
															styles.subCategoryItem,
															isItemActive && styles.subCategoryItemActive
														]}
														onPress={() => handleSubPress(n)}
													>
														<ZenText
															content={n.categoryName}
															style={[
																styles.subCategoryText,
																isItemActive && styles.subCategoryTextActive
															]}
														/>

														{hasChildren && (
															<ZenIcon
																name='caretright'
																type='antdesign'
																size={9}
																color={isItemActive ? theme?.primary : theme?.grey}
															/>
														)}

														{isExactSelection && (
															<ZenIcon
																name='check'
																type='antdesign'
																size={12}
																color={theme?.primary}
															/>
														)}
													</TouchableOpacity>
												);
											})}
										</ScrollView>
									</View>
								</View>
							)}
							popoverStyle={[styles.popoverContent]}
							backgroundStyle={{ backgroundColor: 'transparent' }}
						/>
					);
				}}
			</ThemeConsumer>
		);
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme);
				return (
					<View style={styles.container}>
						<FlatList
							data={data}
							horizontal
							showsHorizontalScrollIndicator={false}
							keyExtractor={(item) => item.id.toString()}
							renderItem={renderItem}
							extraData={{ navPath, openPopoverId, selectedId }} />
					</View>
				);
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) =>
	StyleSheet.create({
		container: {
			marginBottom: 12,
			height: 36,
		},
		popoverContent: {
			borderRadius: 12,
			paddingVertical: 8,
			backgroundColor: theme?.light,
			width: 220,
			shadowColor: '#000',
			shadowOffset: { width: 0, height: 2 },
			shadowOpacity: 0.25,
			shadowRadius: 3.84,
			elevation: 5,
		},
		subCategoryContainer: {
			paddingHorizontal: 4,
		},
		popoverHeader: {
			fontSize: theme?.miniFontSize,
			color: theme?.grey,
			textTransform: 'uppercase',
			fontFamily: theme?.bold,
			paddingHorizontal: 12,
			paddingBottom: 8,
			marginBottom: 4,
			borderBottomWidth: 1,
			borderBottomColor: theme?.disableGray,
		},
		subCategoryItem: {
			paddingVertical: 10,
			paddingHorizontal: 12,
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			borderRadius: 8,
		},
		subCategoryItemActive: {
			backgroundColor: theme?.lightPrimary,
		},
		subCategoryText: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
		},
		subCategoryTextActive: {
			color: theme?.primary,
			fontFamily: theme?.bold,
		},
		chip: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 7,
			backgroundColor: theme?.light,
			paddingHorizontal: 16,
			justifyContent: 'center',
			borderRadius: 20,
			marginRight: 8,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			height: 35,
		},
		chipActive: {
			backgroundColor: theme?.lightPrimary,
			borderColor: theme?.primary,
			borderWidth: 0.7,
		},
		chipText: {
			fontSize: theme?.errorFontSize,
			fontFamily: theme?.medium,
			color: theme?.grey,
		},
		chipTextActive: {
			color: theme?.primary,
			fontFamily: theme?.bold,
		},
	});

export default CategorySelector;